import { useEffect, useMemo, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Compass, ArrowUpDown } from "lucide-react";

const PERIODS = [
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
];

const ACTION_CFG = {
  green: { dot: "bg-emerald-500", pill: "bg-emerald-100 text-emerald-700" },
  amber: { dot: "bg-amber-500", pill: "bg-amber-100 text-amber-700" },
  red: { dot: "bg-red-500", pill: "bg-red-100 text-red-700" },
  blue: { dot: "bg-blue-500", pill: "bg-blue-100 text-blue-700" },
};

const fmtMoney = (n) =>
  `£${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function TerritoryPlanning() {
  const [period, setPeriod] = useState("week");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState("priorityScore");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    setLoading(true);
    apiClient
      .get(`/activity-locations/territory?period=${period}`)
      .then((res) => setRows(res.data?.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [period]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      return sortDir === "asc"
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "priorityScore" || key === "net" || key === "signups" ? "desc" : "asc");
    }
  };

  const SortHeader = ({ label, col, align = "right" }) => (
    <th
      className={`py-2 pr-4 font-medium text-slate-600 cursor-pointer select-none ${align === "right" ? "text-right" : ""}`}
      onClick={() => toggleSort(col)}
      data-testid={`sort-${col}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${sortKey === col ? "text-slate-900" : "text-slate-300"}`} />
      </span>
    </th>
  );

  return (
    <Card className="border-slate-200" data-testid="territory-planning-card">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Compass className="h-5 w-5 text-indigo-600" />
              Territory Planning
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Sorted by priority score (recent signups, profitability, staleness, historical performance).
            </p>
          </div>
          <div className="flex gap-1" data-testid="territory-period-buttons">
            {PERIODS.map((p) => (
              <Button
                key={p.value}
                size="sm"
                variant={period === p.value ? "default" : "outline"}
                onClick={() => setPeriod(p.value)}
                data-testid={`territory-period-${p.value}`}
                className={period === p.value ? "bg-indigo-600 hover:bg-indigo-700" : ""}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && <div className="text-center py-8 text-slate-400">Loading…</div>}
        {!loading && sortedRows.length === 0 && (
          <div className="text-center py-8 text-slate-400" data-testid="territory-empty">
            No locations to plan.
          </div>
        )}
        {!loading && sortedRows.length > 0 && (
          <div className="overflow-x-auto" data-testid="territory-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <SortHeader label="Location" col="locationName" align="left" />
                  <SortHeader label="Signups" col="signups" />
                  <SortHeader label="Net" col="net" />
                  <SortHeader label="Last Staffed" col="lastStaffed" align="left" />
                  <SortHeader label="Days Since" col="daysSinceStaffed" />
                  <th className="py-2 pr-4 font-medium text-slate-600">Action</th>
                  <SortHeader label="Priority" col="priorityScore" />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => {
                  const cfg = ACTION_CFG[r.action] || ACTION_CFG.amber;
                  return (
                    <tr
                      key={r.locationId}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`territory-row-${r.locationId}`}
                    >
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-2">
                          <span className={`inline-block w-2 h-2 rounded-full ${cfg.dot}`} />
                          <div>
                            <div className="font-medium text-slate-900">{r.locationName}</div>
                            {r.locationAddress && (
                              <div className="text-xs text-slate-400">{r.locationAddress}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-right font-mono">{r.signups}</td>
                      <td
                        className={`py-2 pr-4 text-right font-mono ${
                          r.net > 0 ? "text-emerald-700" : r.net < 0 ? "text-red-600" : "text-slate-500"
                        }`}
                      >
                        {fmtMoney(r.net)}
                      </td>
                      <td className="py-2 pr-4 text-slate-600 text-xs">
                        {r.lastStaffed ? r.lastStaffed.slice(0, 10) : "—"}
                      </td>
                      <td className="py-2 pr-4 text-right font-mono text-slate-600">
                        {r.daysSinceStaffed == null ? "—" : r.daysSinceStaffed}
                      </td>
                      <td className="py-2 pr-4">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cfg.pill}`}>
                          {r.actionLabel}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-right font-mono font-semibold text-slate-900">
                        {r.priorityScore}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
